import { useState } from "react";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  Box,
  Chip,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { dayjs } from "@thalamos/common";
import type {
  AuditTrailEntry,
  AuditTrailEntryKind,
} from "@thalamos/haven-common";
import { AuditTrailEntryKind as EntryKind } from "@thalamos/haven-common";

import { apiHooks } from "../../api/index.js";
import { AuditEntryDialog } from "./AuditEntryDialog.js";
import { DirectoryTable } from "./DirectoryTable.js";
import { useTrailSearch } from "./useTrailSearch.js";

/** A page of the trail. Smaller than a directory page: each row is much wider. */
const TRAIL_PAGE_LIMIT = 20;

const entryKinds = Object.values(EntryKind).sort();

/**
 * The people and things an entry names, as one cell.
 *
 * Only the named ones: an entry records a resource's id whether or not its name
 * was known, and an id on its own tells a reader scanning the table nothing that
 * the full entry does not tell them better.
 */
const resourceSummary = (entry: AuditTrailEntry): string =>
  entry.resources
    // `flatMap` rather than filter-then-map: the filter narrows at runtime and
    // not in the type, so the name would still be a possibly-absent value where
    // it is interpolated.
    .flatMap((resource) =>
      resource.resourceName === null || resource.resourceName === undefined
        ? []
        : [`${resource.resourceKind}: ${resource.resourceName}`],
    )
    .join(", ");

/**
 * The audit trail, for Thalamos.
 *
 * Ported from eMHA's viewer and narrowed to what Haven's trail actually holds:
 * there are no teams here, and an entry carries the session context it was made
 * in, so the acting role is a column rather than a detail.
 *
 * The search lives in the URL — see `useTrailSearch` — so a search can be sent
 * to someone else.
 */
export const AuditTrailPage = () => {
  const { t } = useTranslation();
  const search = useTrailSearch();
  const [shown, setShown] = useState<AuditTrailEntry | null>(null);

  const [{ data, loading, error }] = apiHooks.auditTrail.search({
    fromDate: search.fromDate,
    toDate: search.toDate,
    kinds: search.kinds,
    userId: search.searchedUserId,
    resourceId: search.resourceId,
    limit: TRAIL_PAGE_LIMIT,
    offset: search.offset,
  });

  const columns = [
    {
      id: "expand",
      labelKey: "admin.trail.entry",
      render: (entry: AuditTrailEntry) => (
        <IconButton
          size="small"
          aria-label={t("admin.trail.showEntry")}
          onClick={() => {
            setShown(entry);
          }}
        >
          <UnfoldMoreIcon fontSize="small" />
        </IconButton>
      ),
    },
    {
      id: "when",
      labelKey: "admin.trail.when",
      render: (entry: AuditTrailEntry) =>
        dayjs(entry.timestamp).format("DD/MM/YYYY HH:mm:ss"),
    },
    {
      id: "kind",
      labelKey: "admin.trail.kind",
      render: (entry: AuditTrailEntry) => entry.kind,
    },
    {
      id: "who",
      labelKey: "admin.trail.who",
      render: (entry: AuditTrailEntry) => entry.userName ?? "",
    },
    {
      id: "sessionContext",
      labelKey: "admin.trail.sessionContext",
      // The organisation and the roles together: the trail records what someone
      // was acting as, and either half alone does not say it.
      render: (entry: AuditTrailEntry) =>
        entry.organisationName === null || entry.organisationName === undefined
          ? ""
          : `${entry.organisationName}${entry.roles?.length ? ` (${entry.roles.join(", ")})` : ""}`,
    },
    {
      id: "resources",
      labelKey: "admin.trail.resources",
      render: resourceSummary,
    },
  ];

  return (
    <Stack spacing={2}>
      <Typography variant="h5" component="h1">
        {t("admin.trail.title")}
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {/* Native date inputs: the window is two plain dates, and a picker
            component would be a dependency for no gain. */}
        <TextField
          size="small"
          type="date"
          label={t("admin.trail.from")}
          value={search.fromDate}
          onChange={(event) => {
            search.update({ fromDate: event.target.value });
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          size="small"
          type="date"
          label={t("admin.trail.to")}
          value={search.toDate}
          onChange={(event) => {
            search.update({ toDate: event.target.value });
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />

        {/* Bounded, because the chips are the value: without a ceiling the field
            grows with every kind selected and drags the rest of the row out of
            place. It wraps to a second line instead, and scrolls past three. */}
        <Box sx={{ width: 320, maxWidth: "100%" }}>
          <Select
            multiple
            displayEmpty
            fullWidth
            size="small"
            value={search.kinds}
            aria-label={t("admin.trail.kinds")}
            onChange={(event) => {
              search.update({
                kinds: event.target.value as AuditTrailEntryKind[],
              });
            }}
            renderValue={(selected) =>
              selected.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t("admin.trail.allKinds")}
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    maxHeight: 72,
                    overflowY: "auto",
                  }}
                >
                  {selected.map((kind) => (
                    <Chip key={kind} label={kind} size="small" />
                  ))}
                </Box>
              )
            }
          >
            {entryKinds.map((kind) => (
              <MenuItem key={kind} value={kind}>
                {kind}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <TextField
          size="small"
          label={t("admin.trail.userId")}
          value={search.userId}
          onChange={(event) => {
            search.update({ userId: event.target.value });
          }}
          sx={{ minWidth: 320 }}
        />
        <TextField
          size="small"
          label={t("admin.trail.resourceId")}
          value={search.resourceId}
          onChange={(event) => {
            search.update({ resourceId: event.target.value });
          }}
          sx={{ minWidth: 320 }}
        />
      </Box>

      <DirectoryTable
        rows={data?.entries ?? []}
        total={data?.count ?? 0}
        loading={loading}
        error={error !== undefined && error !== null}
        columns={columns}
        rowKey={(entry) => entry.id}
        limit={TRAIL_PAGE_LIMIT}
        offset={search.offset}
        onPageChange={(offset) => {
          search.update({ offset });
        }}
      />

      <AuditEntryDialog
        entry={shown}
        onClose={() => {
          setShown(null);
        }}
      />
    </Stack>
  );
};
